import type {
  RenderDraftStateDto,
  RenderExampleDto,
  RenderJobDto,
  RenderStyleDto,
  StartBuildInput,
  StartBuildResult,
} from '@app/shared';

/**
 * The Mini App's client of the build routes (stage 3). Kept apart from
 * `api-core.ts` and `voice-api.ts`, like stage 4's client. File links come from
 * the API relative to its base and are made absolute here: `<video>` and
 * Telegram's `downloadFile` need a full address.
 */

export class RenderApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'RenderApiError';
  }
}

export interface RenderApiDeps {
  baseUrl: string;
  getInitData: () => string;
  fetchImpl?: typeof fetch;
  /** Where a relative base lives; the page's origin by default. */
  origin?: string;
}

export interface RenderApi {
  getDraftState(draftId: number): Promise<RenderDraftStateDto>;
  /** The example of every format in the style a person starts on (links absolute). */
  getExamples(): Promise<RenderExampleDto[]>;
  startBuild(input: StartBuildInput): Promise<StartBuildResult>;
  getRender(id: number): Promise<RenderJobDto>;
  /** Finished builds, 20 per page, newest first. */
  listRenders(before?: number): Promise<{ items: RenderJobDto[] }>;
  rate(id: number, value: 'up' | 'down', comment?: string): Promise<RenderJobDto>;
  absoluteUrl(url: string): string;
}

export const RENDERS_PAGE = 20;

export function createRenderApi(deps: RenderApiDeps): RenderApi {
  const doFetch = deps.fetchImpl ?? fetch;

  const absoluteUrl = (url: string): string => {
    if (/^https?:\/\//.test(url)) return url;
    const joined = url.startsWith('/files/') ? `${deps.baseUrl}${url}` : url;
    if (/^https?:\/\//.test(joined)) return joined;
    const origin = deps.origin ?? (typeof location !== 'undefined' ? location.origin : '');
    return `${origin}${joined}`;
  };

  const withUrls = (job: RenderJobDto): RenderJobDto => ({
    ...job,
    files: job.files.map((f) => ({ ...f, url: absoluteUrl(f.url), downloadUrl: absoluteUrl(f.downloadUrl) })),
    coverUrl: job.coverUrl ? absoluteUrl(job.coverUrl) : null,
  });

  async function json<T>(method: string, path: string, body?: unknown): Promise<T> {
    let res: Response;
    try {
      res = await doFetch(`${deps.baseUrl}${path}`, {
        method,
        headers: { 'Content-Type': 'application/json', 'X-Init-Data': deps.getInitData() },
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (err) {
      throw new RenderApiError(0, 'NETWORK', err instanceof Error ? err.message : 'Network error');
    }
    if (!res.ok) {
      let code = 'UNKNOWN';
      let message = `HTTP ${res.status}`;
      try {
        const data = (await res.json()) as { error?: { code?: string; message?: string } };
        code = data.error?.code ?? code;
        message = data.error?.message ?? message;
      } catch {
        // The body is not JSON (a proxy error page): keep the status line.
      }
      throw new RenderApiError(res.status, code, message);
    }
    return (await res.json()) as T;
  }

  return {
    getDraftState: async (draftId) => {
      const state = await json<RenderDraftStateDto>('GET', `/renders/drafts/${draftId}`);
      const withLinks = (styles: RenderStyleDto[]) =>
        styles.map((s) => ({
          ...s,
          previewUrl: s.previewUrl ? absoluteUrl(s.previewUrl) : null,
          previewVideoUrl: s.previewVideoUrl ? absoluteUrl(s.previewVideoUrl) : null,
        }));
      return {
        ...state,
        styles: withLinks(state.styles),
        variants: (state.variants ?? []).map((v) => ({ ...v, styles: withLinks(v.styles) })),
      };
    },
    getExamples: async () => {
      const { items } = await json<{ items: RenderExampleDto[] }>('GET', '/renders/examples');
      return items.map((e) => ({
        ...e,
        imageUrl: e.imageUrl ? absoluteUrl(e.imageUrl) : null,
        videoUrl: e.videoUrl ? absoluteUrl(e.videoUrl) : null,
      }));
    },
    startBuild: async (input) => {
      const r = await json<StartBuildResult>('POST', '/renders', input);
      return r.kind === 'ok' ? { kind: 'ok', job: withUrls(r.job) } : r;
    },
    getRender: async (id) => withUrls(await json<RenderJobDto>('GET', `/renders/${id}`)),
    listRenders: async (before) => {
      const query = `?limit=${RENDERS_PAGE}${before !== undefined ? `&before=${before}` : ''}`;
      const page = await json<{ items: RenderJobDto[] }>('GET', `/renders${query}`);
      return { items: page.items.map(withUrls) };
    },
    rate: async (id, value, comment) =>
      withUrls(await json<RenderJobDto>('POST', `/renders/${id}/rating`, comment ? { value, comment } : { value })),
    absoluteUrl,
  };
}
